// Reusable phone-number input directive (v-phone).
//
// Root-cause fix for the "Ctrl+A then type doesn't replace" bug: the old
// per-page `onlyNumbers` keypress guard blocked new input whenever the field
// already held 11 digits — it measured the whole value and ignored the current
// selection, so replacing a selected value was impossible.
//
// This directive instead SANITIZES on `input` (after the browser has applied the
// edit, including selection-replacement / paste / mobile select-all / backspace):
// it keeps digits only (western + Arabic-Indic) and caps at 11 digits, then
// re-emits `input` so v-model / validation stay in sync. Because the browser has
// already replaced the selection by the time we run, select-all + type works
// naturally, and 11-digit + numeric-only behavior is preserved.

const MAX_DIGITS = 11;
const DIGITS_RE = /[^0-9٠-٩]/g;

function resolveInput(el) {
  return el.tagName === 'INPUT' ? el : el.querySelector('input');
}

function sanitize(input) {
  const raw = input.value;
  let digits = raw.replace(DIGITS_RE, '');
  if (digits.length > MAX_DIGITS) digits = digits.slice(0, MAX_DIGITS);
  if (digits !== raw) {
    input.value = digits;
    // Keep the caret at the end after a correction (phone fields are simple,
    // LTR, append-style; only runs when we actually changed something).
    try { input.setSelectionRange(digits.length, digits.length); } catch (e) { /* non-text input */ }
    return true;
  }
  return false;
}

function handler(e) {
  // Ignore the synthetic event we dispatch below (prevents recursion).
  if (e.__phoneSanitized) return;
  const input = e.target;
  if (sanitize(input)) {
    const ev = new Event('input', { bubbles: true });
    ev.__phoneSanitized = true;
    input.dispatchEvent(ev);
  }
}

export default {
  mounted(el) {
    const input = resolveInput(el);
    if (!input) return;
    input.addEventListener('input', handler);
    el.__phoneInputEl = input;
  },
  unmounted(el) {
    if (el.__phoneInputEl) {
      el.__phoneInputEl.removeEventListener('input', handler);
      el.__phoneInputEl = null;
    }
  },
};
